<template>
  <div class="ai-chat-test">
    <div class="container-fluid py-4">
      <h2 class="h4 fw-medium mb-4">{{ $t("__aiChatTest") || "AI 對話測試" }}</h2>
      
      <!-- 設定區 -->
      <div class="row mb-4">
        <div class="col-lg-4">
          <div class="card shadow-sm">
            <div class="card-header bg-primary text-white">
              <i class="bi bi-gear-fill me-2"></i>{{ $t("__aiChatTestSettings") }}
            </div>
            <div class="card-body">
              <!-- 選擇機構 -->
              <div class="mb-3">
                <label class="form-label">{{ $t("__aiChatTestAgency") }}</label>
                <select v-model="testAgencyId" @change="loadAiSettings" class="form-select">
                  <option :value="null">{{ $t("__globalSetting") }}</option>
                  <option v-for="agency in allAgencys" :value="agency.id" :key="agency.id">
                    {{ agency.name }}
                  </option>
                </select>
              </div>

              <!-- 選擇 AI 設定 -->
              <div class="mb-3">
                <label class="form-label">{{ $t("__aiChatAiSetting") }}</label>
                <select v-model="selectedSettingId" @change="selectSetting" class="form-select">
                  <option :value="null">{{ $t("__aiChatSelectSetting") }}</option>
                  <option v-for="setting in aiSettings" :value="setting.id" :key="setting.id">
                    {{ setting.provider.toUpperCase() }} - {{ setting.model }}
                    <span v-if="!setting.is_active">{{ $t("__aiChatInactive") }}</span>
                  </option>
                </select>
              </div>

              <!-- 模擬身份 -->
              <div class="mb-3">
                <label class="form-label">{{ $t("__aiChatSimulateIdentity") }}</label>
                <div class="form-check">
                  <input
                    v-model="simulateAsStaff"
                    type="checkbox"
                    class="form-check-input"
                    id="simulateStaff"
                  >
                  <label class="form-check-label" for="simulateStaff">
                    {{ $t("__aiChatSimulateAsStaff") }}
                  </label>
                </div>
              </div>

              <!-- 客服人員選擇 -->
              <div v-if="simulateAsStaff" class="mb-3">
                <label class="form-label">{{ $t("__aiChatSelectStaff") }}</label>
                <select v-model="selectedStaffId" class="form-select">
                  <option :value="null">{{ $t("__selectPlaceholder") }}</option>
                  <option v-for="staff in staffMembers" :value="staff.line_user_id" :key="staff.id">
                    {{ staff.staff_name || staff.line_display_name }} ({{ staff.line_user_id }})
                  </option>
                </select>
              </div>

              <!-- 模擬群組 -->
              <div class="mb-3">
                <label class="form-label">{{ $t("__aiChatGroupIdLabel") }}</label>
                <input
                  v-model="testGroupId"
                  type="text"
                  class="form-control"
                  :placeholder="$t('__aiChatGroupIdPlaceholder')"
                >
                <small class="text-muted">{{ $t("__aiChatGroupIdHint") }}</small>
              </div>
              
              <!-- 當前設定資訊 -->
              <div v-if="currentSetting" class="alert alert-info small">
                <div><strong>Provider:</strong> {{ currentSetting.provider }}</div>
                <div><strong>Model:</strong> {{ currentSetting.model }}</div>
                <div><strong>API Key:</strong> {{ currentSetting.api_key ? $t('__aiChatApiKeyConfigured') : $t('__aiChatApiKeyNotConfigured') }}</div>
                <div><strong>{{ $t("__aiChatTriggerConditions") }}</strong></div>
                <ul class="mb-0">
                  <li v-if="currentSetting.trigger_on_question">{{ $t("__aiChatTriggerOnQuestion") }}</li>
                  <li v-if="currentSetting.trigger_on_mention">{{ $t("__aiChatTriggerOnMention") }}</li>
                  <li v-if="currentSetting.trigger_keywords && currentSetting.trigger_keywords.length">
                    {{ $t("__aiChatTriggerKeywords") }}: {{ currentSetting.trigger_keywords.join(', ') }}
                  </li>
                  <li v-if="simulateAsStaff && currentSetting.staff_trigger_keywords">
                    {{ $t("__aiChatStaffKeywords") }}: {{ currentSetting.staff_trigger_keywords.join(', ') }}
                  </li>
                </ul>
              </div>

              <!-- 暫停狀態 -->
              <div v-if="pauseInfo" class="alert alert-warning">
                <i class="bi bi-pause-circle me-2"></i>
                {{ $t("__aiChatPaused") }}
                <div class="small mt-1">
                  {{ $t("__aiChatPausedBy") }}：{{ pauseInfo.staff_name || pauseInfo.staff_id }}<br>
                  {{ $t("__aiChatRemainingTime") }}：{{ $t("__aiChatRemainingSeconds", { seconds: pauseInfo.remaining_seconds }) }}
                </div>
                <button
                  @click="resumeAi"
                  class="btn btn-sm btn-success mt-2"
                >
                  {{ $t("__aiChatResumeAi") }}
                </button>
              </div>

              <!-- 操作按鈕 -->
              <div class="d-grid gap-2">
                <button
                  @click="clearChat"
                  class="btn btn-outline-danger"
                >
                  <i class="bi bi-trash me-2"></i>{{ $t("__aiChatClearChat") }}
                </button>
                <button
                  v-if="!pauseInfo"
                  @click="pauseAi"
                  class="btn btn-outline-warning"
                >
                  <i class="bi bi-pause me-2"></i>{{ $t("__aiChatPauseAi") }}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 對話區 -->
        <div class="col-lg-8">
          <div class="card shadow-sm chat-container">
            <div class="card-header bg-white border-bottom">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <i class="bi bi-chat-dots-fill text-primary me-2"></i>
                  <strong>{{ $t("__aiChatTest") }}</strong>
                  <span v-if="testGroupId" class="badge bg-info ms-2">{{ $t("__aiChatGroupMode") }}</span>
                  <span v-if="simulateAsStaff" class="badge bg-warning ms-2">{{ $t("__aiChatStaffMode") }}</span>
                </div>
                <div class="text-muted small">
                  <span v-if="typing" class="me-3">
                    <i class="bi bi-three-dots typing-indicator"></i> {{ $t("__aiChatTyping") }}
                  </span>
                  {{ $t("__aiChatMessageCount", { count: messages.length }) }}
                </div>
              </div>
            </div>
            
            <div class="card-body chat-messages" ref="chatMessages">
              <!-- 無訊息提示 -->
              <div v-if="messages.length === 0" class="text-center text-muted py-5">
                <i class="bi bi-chat-text fs-1"></i>
                <p class="mt-3">{{ $t("__aiChatStartTest") }}</p>
                <small>{{ $t("__aiChatStartTestHint") }}</small>
              </div>

              <!-- 訊息列表 -->
              <div v-for="(message, index) in messages" :key="index" class="message-item mb-3">
                <div v-if="message.type === 'user'" class="d-flex justify-content-end">
                  <div class="message-bubble user-message">
                    <div class="message-header">
                      <span class="badge bg-white bg-opacity-25 text-white me-2">
                        {{ message.isStaff ? $t('__aiChatStaffLabel') : $t('__aiChatUserLabel') }}
                      </span>
                      <small>{{ formatTime(message.timestamp) }}</small>
                    </div>
                    <div class="message-content">{{ message.content }}</div>
                    <div v-if="message.metadata" class="message-metadata">
                      <small>
                        <span v-if="message.metadata.triggered">
                          <i class="bi bi-check-circle"></i> {{ $t("__aiChatTriggered") }}
                        </span>
                        <span v-else>
                          <i class="bi bi-x-circle"></i> {{ $t("__aiChatNotTriggered") }}
                        </span>
                      </small>
                    </div>
                  </div>
                </div>

                <div v-else-if="message.type === 'ai'" class="d-flex justify-content-start">
                  <div class="message-bubble ai-message">
                    <div class="message-header">
                      <i class="bi bi-robot me-1"></i>
                      <strong>{{ $t("__aiChatAiAssistant") }}</strong>
                      <small class="text-muted ms-2">{{ formatTime(message.timestamp) }}</small>
                    </div>
                    <div class="message-content">{{ message.content }}</div>
                    <div v-if="message.metadata" class="message-metadata">
                      <small class="text-muted d-flex flex-wrap align-items-center">
                        <span v-if="message.metadata.model">
                          <i class="bi bi-cpu me-1"></i>{{ message.metadata.model }}
                        </span>
                        <span v-if="message.metadata.confidence" class="ms-2">
                          <i class="bi bi-speedometer2 me-1"></i>{{ $t("__aiChatConfidence") }}: {{ (message.metadata.confidence * 100).toFixed(0) }}%
                        </span>
                        <span v-if="message.metadata.related_knowledge && message.metadata.related_knowledge.length" class="ms-2">
                          <i class="bi bi-book me-1"></i>{{ $t("__aiChatReference") }}: {{ $t("__aiChatReferenceCount", { count: message.metadata.related_knowledge.length }) }}
                        </span>
                        <span v-if="message.metadata.response_time" class="ms-2">
                          <i class="bi bi-clock me-1"></i>{{ message.metadata.response_time }}ms
                        </span>
                        <span v-if="message.metadata.conversation_id" class="ms-2">
                          <i class="bi bi-hash me-1"></i>ID: {{ message.metadata.conversation_id }}
                        </span>
                      </small>
                    </div>
                  </div>
                </div>
                
                <div v-else-if="message.type === 'system'" class="text-center">
                  <div class="badge bg-secondary">
                    {{ message.content }}
                  </div>
                </div>
                
                <div v-else-if="message.type === 'error'" class="d-flex justify-content-center">
                  <div class="alert alert-danger">
                    <i class="bi bi-exclamation-triangle me-2"></i>
                    {{ message.content }}
                  </div>
                </div>
              </div>
              
              <!-- 載入指示器 -->
              <div v-if="loading" class="text-center py-3">
                <div class="spinner-border spinner-border-sm text-primary" role="status">
                  <span class="visually-hidden">{{ $t("__aiChatProcessing") }}</span>
                </div>
              </div>
            </div>

            <div class="card-footer bg-white">
              <form @submit.prevent="sendMessage" class="input-group">
                <input
                  v-model="inputMessage"
                  type="text"
                  class="form-control"
                  :placeholder="$t('__aiChatInputPlaceholder')"
                  :disabled="!currentSetting || loading"
                  ref="messageInput"
                >
                <button
                  type="submit"
                  class="btn btn-primary"
                  :disabled="!inputMessage.trim() || !currentSetting || loading"
                >
                  <i class="bi bi-send-fill"></i>
                </button>
              </form>

              <!-- 快速測試按鈕 -->
              <div class="mt-2">
                <small class="text-muted me-2">{{ $t("__aiChatQuickTest") }}</small>
                <button
                  @click="inputMessage = $t('__aiChatQuickHello')"
                  class="btn btn-sm btn-outline-secondary me-1 mb-1"
                >
                  {{ $t("__aiChatQuickHello") }}
                </button>
                <button
                  @click="inputMessage = $t('__aiChatQuickBusinessHours')"
                  class="btn btn-sm btn-outline-secondary me-1 mb-1"
                >
                  {{ $t("__aiChatQuickBusinessHours") }}
                </button>
                <button
                  @click="inputMessage = $t('__aiChatQuickContact')"
                  class="btn btn-sm btn-outline-secondary me-1 mb-1"
                >
                  {{ $t("__aiChatQuickContact") }}
                </button>
                <button
                  @click="inputMessage = $t('__aiChatQuickPrice')"
                  class="btn btn-sm btn-outline-secondary me-1 mb-1"
                >
                  {{ $t("__aiChatQuickPrice") }}
                </button>
                <button
                  @click="inputMessage = $t('__aiChatQuickMention')"
                  class="btn btn-sm btn-outline-secondary me-1 mb-1"
                >
                  {{ $t("__aiChatQuickMention") }}
                </button>
                <button
                  @click="inputMessage = $t('__aiChatQuickWhat')"
                  class="btn btn-sm btn-outline-secondary me-1 mb-1"
                >
                  {{ $t("__aiChatQuickWhat") }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'
import { aiChat } from '@/http/api/aiChat.js'
import { aiSettings } from '@/http/api/aiSettings.js'
import { aiStaffMembers } from '@/http/api/aiStaffMembers.js'
import { agency } from '@/http/api/agency.js'

export default {
  name: 'AiChatTest',
  computed: {
    ...mapState(['token', 'agencys'])
  },
  data() {
    return {
      // 設定相關
      allAgencys: [],
      aiSettings: [],
      staffMembers: [],
      testAgencyId: null,
      selectedSettingId: null,
      currentSetting: null,
      simulateAsStaff: false,
      selectedStaffId: null,
      testGroupId: '',
      
      // 對話相關
      messages: [],
      inputMessage: '',
      loading: false,
      typing: false,
      
      // 暫停相關
      pauseInfo: null,
      pauseCheckInterval: null,
      
      // 統計
      statistics: {
        total_messages: 0,
        ai_responses: 0,
        avg_response_time: 0,
        success_rate: 100
      },
      
      // 快速測試
      quickTests: [
        '你好',
        '請問營業時間？',
        '如何聯絡客服？',
        '產品價格是多少？',
        '@AI 請協助',
        '這是什麼？'
      ]
    }
  },
  mounted() {
    this.loadAgencies()
    this.loadAiSettings()
    this.loadStaffMembers()
    
    // 每30秒檢查暫停狀態
    this.pauseCheckInterval = setInterval(() => {
      if (this.testGroupId) {
        this.checkPauseStatus()
      }
    }, 30000)
  },
  beforeDestroy() {
    if (this.pauseCheckInterval) {
      clearInterval(this.pauseCheckInterval)
    }
  },
  methods: {
    async loadAgencies() {
      try {
        const response = await agency.get(null, this.token)
        this.allAgencys = response.data.data || response.data
      } catch (error) {
        console.error(this.$t('__aiChatLoadAgencyFailed') + ':', error)
      }
    },

    async loadAiSettings() {
      try {
        const params = {}
        if (this.testAgencyId) {
          params.agency_id = this.testAgencyId
        }

        const response = await aiSettings.get(params, this.token)
        this.aiSettings = response.data.data || response.data

        // 自動選擇第一個啟用的設定
        const activeSetting = this.aiSettings.find(s => s.is_active)
        if (activeSetting) {
          this.selectedSettingId = activeSetting.id
          this.selectSetting()
        }
      } catch (error) {
        console.error(this.$t('__aiChatLoadSettingFailed') + ':', error)
      }
    },

    async loadStaffMembers() {
      try {
        const params = { is_active: true }
        if (this.testAgencyId) {
          params.agency_id = this.testAgencyId
        }

        const response = await aiStaffMembers.get(params, this.token)
        this.staffMembers = response.data.data || []
      } catch (error) {
        console.error(this.$t('__aiChatLoadStaffFailed') + ':', error)
      }
    },

    selectSetting() {
      this.currentSetting = this.aiSettings.find(s => s.id === this.selectedSettingId)
      if (this.currentSetting && !this.currentSetting.api_key) {
        this.addSystemMessage(this.$t('__aiChatApiKeyWarning'))
      }
    },
    
    async sendMessage() {
      if (!this.inputMessage.trim() || !this.currentSetting) return
      
      const userMessage = this.inputMessage
      this.inputMessage = ''
      
      // 加入使用者訊息
      this.addUserMessage(userMessage)
      
      // 發送到後端
      this.loading = true
      this.typing = true
      
      try {
        const startTime = Date.now()
        
        // 根據測試模式選擇不同的 API
        let response

        if (this.testGroupId) {
          // 模擬 Line 群組訊息
          response = await aiChat.simulateLine({
            message: userMessage,
            group_id: this.testGroupId,
            user_id: this.simulateAsStaff ? this.selectedStaffId : 'test_user',
            is_staff: this.simulateAsStaff,
            agency_id: this.testAgencyId,
            setting_id: this.selectedSettingId
          }, this.token)
        } else {
          // 直接 AI 對話
          response = await aiChat.chat({
            message: userMessage,
            agency_id: this.testAgencyId,
            simulate_staff: this.simulateAsStaff,
            simulate_group_id: this.testGroupId,
            user_id: this.simulateAsStaff ? this.selectedStaffId : 'test_user'
          }, this.token)
        }
        
        const responseTime = Date.now() - startTime

        // 處理成功的回應
        if (response.data.triggered_ai === false) {
          // AI 未觸發
          let reason = response.data.message || this.$t('__aiChatNotTriggeredReason', { reason: '' })

          // 根據不同原因顯示不同的訊息
          if (response.data.staff_trigger_required) {
            reason = this.$t('__aiChatStaffKeywordRequired', { keywords: response.data.staff_trigger_keywords?.join(', ') || '' })
          } else if (response.data.user_direct_trigger === false) {
            reason = this.$t('__aiChatTriggerKeywordRequired', { keywords: response.data.trigger_keywords?.join(', ') || '' })
          }

          this.addSystemMessage(reason)
        } else if (response.data.success) {
          // 檢查是否有 AI 回應
          let aiReply = null
          let metadata = {}

          if (this.testGroupId && response.data.ai_response) {
            // 模擬 Line 群組模式 - ai_response 是物件
            if (response.data.ai_response.success && response.data.ai_response.response) {
              aiReply = response.data.ai_response.response
              metadata = {
                model: 'gpt-4o-mini',
                confidence: response.data.ai_response.confidence,
                conversation_id: response.data.ai_response.conversation_id,
                related_knowledge: response.data.ai_response.related_knowledge,
                response_time: responseTime,
                is_staff: response.data.is_staff,
                trigger_reason: response.data.trigger_reason
              }
            }
          } else if (response.data.response) {
            // 直接對話模式
            aiReply = response.data.response
            metadata = {
              model: response.data.model || 'gpt-4o-mini',
              tokens: response.data.usage?.total_tokens,
              response_time: responseTime,
              confidence: response.data.confidence,
              conversation_id: response.data.conversation_id,
              related_knowledge: response.data.related_knowledge,
              is_staff: response.data.is_staff
            }
          }

          if (aiReply) {
            this.addAiMessage(aiReply, metadata)
            // 更新統計
            this.statistics.ai_responses++
            this.updateAverageResponseTime(responseTime)
          } else if (response.data.should_trigger === false) {
            // 未觸發 AI
            this.addSystemMessage(this.$t('__aiChatNotTriggeredReason', { reason: response.data.trigger_reason || '' }))
          }
        } else if (response.data.is_paused) {
          // AI 暫停中
          this.addSystemMessage(this.$t('__aiChatPausedNoResponse'))
          this.pauseInfo = response.data.pause_info
        } else if (response.data.message) {
          // 錯誤訊息
          this.addSystemMessage(response.data.message)
        } else {
          // 未知錯誤
          console.error('Unexpected response:', response.data)
          this.addSystemMessage(this.$t('__aiChatUnknownError'))
        }
        
        // 更新訊息 metadata
        if (this.messages.length > 0) {
          const lastUserMessage = this.messages[this.messages.length - 2]
          if (lastUserMessage && lastUserMessage.type === 'user') {
            lastUserMessage.metadata = {
              triggered: response.data.triggered !== false
            }
          }
        }
        
      } catch (error) {
        console.error(this.$t('__aiChatSendFailed') + ':', error)

        let errorMessage = this.$t('__aiChatSendFailed')
        if (error.response) {
          if (error.response.status === 422) {
            errorMessage = error.response.data.message || this.$t('__aiChatRequestError')
          } else if (error.response.status === 500) {
            errorMessage = this.$t('__aiChatServiceError')
          } else {
            errorMessage = error.response.data.message || this.$t('__aiChatUnknownError')
          }
        }

        this.addErrorMessage(errorMessage)

        // 更新失敗率
        this.updateSuccessRate(false)
      } finally {
        this.loading = false
        this.typing = false
      }
      
      this.statistics.total_messages++
    },
    
    addUserMessage(content) {
      this.messages.push({
        type: 'user',
        content: content,
        timestamp: new Date(),
        isStaff: this.simulateAsStaff
      })
      this.scrollToBottom()
    },
    
    addAiMessage(content, metadata = {}) {
      this.messages.push({
        type: 'ai',
        content: content,
        timestamp: new Date(),
        metadata: metadata
      })
      this.scrollToBottom()
      this.updateSuccessRate(true)
    },
    
    addSystemMessage(content) {
      this.messages.push({
        type: 'system',
        content: content,
        timestamp: new Date()
      })
      this.scrollToBottom()
    },
    
    addErrorMessage(content) {
      this.messages.push({
        type: 'error',
        content: content,
        timestamp: new Date()
      })
      this.scrollToBottom()
    },
    
    getConversationContext() {
      // 取得最近5則對話作為上下文
      const recentMessages = this.messages.slice(-10).filter(m => 
        m.type === 'user' || m.type === 'ai'
      ).map(m => ({
        role: m.type === 'user' ? 'user' : 'assistant',
        content: m.content
      }))
      
      return recentMessages
    },
    
    clearChat() {
      if (confirm(this.$t('__aiChatClearConfirm'))) {
        this.messages = []
        this.statistics = {
          total_messages: 0,
          ai_responses: 0,
          avg_response_time: 0,
          success_rate: 100
        }
      }
    },

    async pauseAi() {
      if (!this.testGroupId) {
        alert(this.$t('__aiChatGroupIdRequired'))
        return
      }
      
      try {
        const response = await aiChat.pause({
          group_id: this.testGroupId,
          action: 'pause',
          duration: 300 // 5分鐘
        }, this.token)
        
        this.pauseInfo = {
          remaining_seconds: 300,
          staff_id: '手動暫停'
        }
        
        this.addSystemMessage(response.data.message)
      } catch (error) {
        this.$store.dispatch('alert', {
          type: 'danger',
          message: this.$t('__aiChatPauseFailed')
        })
      }
    },

    async resumeAi() {
      if (!this.testGroupId) return

      try {
        const response = await aiChat.pause({
          group_id: this.testGroupId,
          action: 'resume'
        }, this.token)

        this.pauseInfo = null
        this.addSystemMessage(response.data.message)
      } catch (error) {
        this.$store.dispatch('alert', {
          type: 'danger',
          message: this.$t('__aiChatResumeFailed')
        })
      }
    },

    async checkPauseStatus() {
      if (!this.testGroupId) return

      try {
        const response = await aiChat.pauseStatus(
          { group_id: this.testGroupId },
          this.token
        )

        this.pauseInfo = response.data.is_paused ? response.data.pause_info : null
      } catch (error) {
        console.error(this.$t('__aiChatCheckPauseFailed') + ':', error)
      }
    },
    
    updateAverageResponseTime(newTime) {
      const count = this.statistics.ai_responses
      const oldAvg = this.statistics.avg_response_time || 0
      this.statistics.avg_response_time = Math.round(
        (oldAvg * (count - 1) + newTime) / count
      )
    },
    
    updateSuccessRate(success) {
      const total = this.statistics.ai_responses + (success ? 0 : 1)
      const successful = this.statistics.ai_responses
      this.statistics.success_rate = total > 0 
        ? Math.round((successful / total) * 100) 
        : 100
    },
    
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatMessages
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },
    
    formatTime(timestamp) {
      return moment(timestamp).format('HH:mm:ss')
    }
  }
}
</script>

<style scoped>
.ai-chat-test {
  min-height: calc(100vh - 100px);
}

.chat-container {
  height: 600px;
  display: flex;
  flex-direction: column;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #f8f9fa;
}

.message-item {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message-bubble {
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 12px;
  word-wrap: break-word;
}

.user-message {
  background: #007bff;
  color: white !important;
  border-bottom-right-radius: 4px;
}

.user-message * {
  color: white !important;
}

.ai-message {
  background: white;
  border: 1px solid #dee2e6;
  border-bottom-left-radius: 4px;
}

.message-header {
  font-size: 0.85rem;
  margin-bottom: 6px;
}

.user-message .message-header {
  color: white !important;
  font-weight: 500;
}

.user-message .message-header small {
  color: white !important;
  opacity: 1 !important;
}

.ai-message .message-header {
  color: #6c757d;
}

.message-content {
  line-height: 1.5;
}

.message-metadata {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  font-size: 0.75rem;
}

.user-message .message-metadata {
  border-top-color: rgba(255, 255, 255, 0.3);
  color: white !important;
  font-weight: 400;
}

.user-message .message-metadata small {
  color: white !important;
  opacity: 1 !important;
}

.user-message .message-metadata i {
  color: white !important;
}

.typing-indicator {
  animation: typing 1.4s infinite;
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-10px);
  }
}

.card {
  border: none;
}

.card-header {
  font-weight: 500;
}
</style>